// node_modules
import { FC, useCallback, useContext, useEffect, useState } from "react";
import { faCalendarAlt } from "@fortawesome/free-regular-svg-icons";
import { faCircleNotch, faRuler, faSatellite } from "@fortawesome/free-solid-svg-icons";
// Components
import { FindestBookmark, BorderedIconedTextblock } from "Components";
import { PatentDocumentContent } from "./PatentDocumentContent";
// Controllers
import { ArticleControllerSingleton, ArticleApplicationControllerSingleton } from "Controllers";
// Types
import { TUsPatentDTO } from "Types";
// styles
import styles from "./usPatentDocument.module.scss";
import variables from "styles/colors.module.scss";
import "styles/documentHighlights.scss";
// Contexts
import { SavedQueryContext, WebsocketContext } from "Providers";
import { CaseContext, AuthContext } from "Stores";
// Enums
import { EntityTypeEnum } from "Enums";
// Logics
import { SavedQueryLogicSingleton, BatchFunctionLogicSingleton } from "Logics";
// Constants
import { RegexConstants } from "Constants";
// Custom Hooks
import { useTextSelection } from "Hooks";

type TUsPatentDocumentProps = {
    patentDto: TUsPatentDTO,
    onClick?: (documentId: string) => void,
    handlePatentHasBeenUnbookmarked?: (unbookmarkOnEntityId: string) => Promise<void>
    handlePatentHasBeenBookmarked?: (bookmarkOnEntityId: string) => Promise<void>
}

export const UsPatentDocument: FC<TUsPatentDocumentProps> = ({ patentDto, onClick, handlePatentHasBeenUnbookmarked, handlePatentHasBeenBookmarked }: TUsPatentDocumentProps) => {
    const [usPatent, setUsPatent] = useState<TUsPatentDTO | null>(null);
    const [highlightedAbstract, setHighlightedAbstract] = useState<string | undefined>(undefined);
    const [highlightedClaims, setHighlightedClaims] = useState<string | undefined>(undefined);
    const [predictedTechnologies, setPredictedTechnologies] = useState<string[] | undefined>(undefined);
    const [predictedUnits, setPredictedUnits] = useState<string[] | undefined>(patentDto.foundUnits);

    // Contexts
    const { caseState } = useContext(CaseContext);
    const { authState } = useContext(AuthContext);
    const { savedQuery } = useContext(SavedQueryContext);
    const { webSocketController } = useContext(WebsocketContext);

    // Let
    const { setSelectedTextOnMouseEvent } = useTextSelection();
    
    const onClickDocument = async() => {
        if (usPatent === null) {
            if (onClick) { onClick(patentDto.id); }
            const fullDocument = await ArticleControllerSingleton.getUsPatentAsync(patentDto.id);
            if (fullDocument.issueDate) { fullDocument.issueDate = new Date(fullDocument.issueDate); }
            if (fullDocument.filingDate) { fullDocument.filingDate = new Date(fullDocument.filingDate); }
            setUsPatent(fullDocument);
        } else {
            setUsPatent(null);
        }
    };

    const receiveHighlightResult = useCallback((result: string, resultType: number) => {
        const parsedJson: { [key: string]: any } = JSON.parse(result);
        let abstract = "";
        if ("abstract" in parsedJson) {
            abstract = parsedJson["abstract"];
        }
        let claims = "";
        if("claims" in parsedJson) {
            claims = parsedJson["claims"];
        }

        if(predictedUnits) {
            for (const foundUnit of predictedUnits) {
                abstract = abstract.replace(foundUnit, `<span class='phrase-highlight'>${foundUnit}</span>`);
            }

            for (const foundUnit of predictedUnits) {
                claims = claims.replace(foundUnit, `<span class='phrase-highlight'>${foundUnit}</span>`);
            }
        }

        if ("abstract" in parsedJson) setHighlightedAbstract(abstract);
        if ("claims" in parsedJson) setHighlightedClaims(claims);
    }, [predictedUnits]);


    const receiveHighlightedTechnologies = useCallback((result: string) => {
        const predictedTechnologyResult: { [key: string]: any } = JSON.parse(result);
        const newPredictedTechnologies: string[] = [];
        if ("tentity" in predictedTechnologyResult) {
            for (let i = 0; i < predictedTechnologyResult["tentity"].length; i++) {
                const technologyCollection = predictedTechnologyResult["tentity"][i];

                for (let j = 0; j < technologyCollection["technology"].length; j++) {
                    const technology = technologyCollection["technology"][0];
                    const technologyText = technology[1].trim();
                    if (technologyText.length === 0 || technologyText === "UNKNOWN") { continue; }
                    newPredictedTechnologies.push(technologyText);
                }
            }
        }

        setPredictedTechnologies(newPredictedTechnologies);
    }, []);

    const receiveRecognizedUnits = useCallback((result: string) => {
        const foundUnitResult: { [key: string]: any } = JSON.parse(result);
        const foundUnits: string[] = [];
        const documentIds = Object.keys(foundUnitResult);
        for (let i = 0; i < documentIds.length; i++) {
            const documentId = documentIds[i];

            const units = foundUnitResult[documentId];
            for (let j = 0; j < units.length; j++) {
                const unit = units[j];
                foundUnits.push(unit);
            }
        }

        setPredictedUnits(foundUnits);
    }, []);


    const subscribeToResultFunctions = useCallback(async() => {
        await webSocketController.addHandler(`ReceiveHighlightResult${patentDto.id}`, receiveHighlightResult);
        await webSocketController.addHandler(`ReceivePredictedTechnologies${patentDto.id}`, receiveHighlightedTechnologies);
        await webSocketController.addHandler(`ReceivePredictedUnits${patentDto.id}`, receiveRecognizedUnits);
    }, [receiveHighlightResult, receiveHighlightedTechnologies, receiveRecognizedUnits, webSocketController, patentDto.id]);

    const unsubscribeToResultFunctions = useCallback(() => {
        webSocketController.removeHandler(`ReceiveHighlightResult${patentDto.id}`, receiveHighlightResult);
        webSocketController.removeHandler(`ReceivePredictedTechnologies${patentDto.id}`, receiveHighlightedTechnologies);
        webSocketController.removeHandler(`ReceivePredictedUnits${patentDto.id}`, receiveRecognizedUnits);
    }, [receiveHighlightResult, receiveHighlightedTechnologies, receiveRecognizedUnits, webSocketController, patentDto.id]);

    const requestHighlighting = useCallback(() => {
        const isMagPatent = RegexConstants.IS_DIGITS_ONLY.test(patentDto.id.trim());
        const documentType = isMagPatent ? 1 : 2;

        const mainActionObject = savedQuery ? SavedQueryLogicSingleton.determineMainActionObject(savedQuery) : [];
        if (predictedTechnologies === undefined && mainActionObject.length !== 0) {
            const modelType = savedQuery?.filters.technologyFilters.modelType;

            BatchFunctionLogicSingleton.addFunction("TechnologyPrediction", async () => {
                if(caseState.selectedCaseId) {
                    ArticleApplicationControllerSingleton.predictTechnology(webSocketController, caseState.selectedCaseId, 
                        patentDto.id, documentType, mainActionObject[0], mainActionObject[1], modelType);
                }
            });

            BatchFunctionLogicSingleton.startIfNeeded("TechnologyPrediction", 2, 500);
            setPredictedTechnologies([]);
        }

        if (predictedUnits === undefined && savedQuery) {
            //BatchFunctionLogicSingleton.addFunction("UnitPrediction", async() => {
            //    ArticleApplicationControllerSingleton.findUsedUnits(webSocketController, caseState.selectedCaseId!, patentDto.id, documentType, savedQuery.guid);
            //});
//
            //BatchFunctionLogicSingleton.startIfNeeded("UnitPrediction");
            //setPredictedUnits([]);
        }
    }, [caseState.selectedCaseId, patentDto.id, predictedTechnologies, predictedUnits, savedQuery, webSocketController]);

    useEffect(() => {
        requestHighlighting();
    }, [requestHighlighting]);

    useEffect(() => {
        subscribeToResultFunctions();

        return () => unsubscribeToResultFunctions();
    }, [subscribeToResultFunctions, unsubscribeToResultFunctions]);

    return (
        <div className={styles.usPatentDocument}>
            <div className={styles.documentHeader}>
                <div className={styles.documentBookmarkContainer}>
                    <FindestBookmark
                        entity={patentDto}
                        entityType={EntityTypeEnum.PatentType}
                        isEntityBookmarked={patentDto.isBookmarked}
                        isEditable={authState.hasAccessToDynamicDelivery && caseState.isDynamicDelivery}
                        coworkerBookmarkOn={patentDto.coworkerBookmarkOn}
                        scoutBookmarkOn={patentDto.scoutBookmarkOn}
                        handleEntityHasBeenUnbookmarked={handlePatentHasBeenUnbookmarked} 
                        handleEntityHasBeenBookmarked={handlePatentHasBeenBookmarked} />
                </div>
                <div className={styles.documentTitleInformationContainer}>
                    <div className={styles.documentTitleContainer}>
                        <span onMouseUp={setSelectedTextOnMouseEvent} className={styles.documentTitle} onClick={onClickDocument}>{patentDto.title}</span>
                        {patentDto.year ?
                            <BorderedIconedTextblock icon={faCalendarAlt} text={`${patentDto.year}`} />
                            : <></>
                        }
                    </div>
                    <div className={styles.documentMetaDataContainer}>
                        {predictedTechnologies ?
                            predictedTechnologies.length > 0 ?
                                predictedTechnologies.map((tech, index) => {
                                    return (<BorderedIconedTextblock key={index} onMouseUpHandler={setSelectedTextOnMouseEvent} icon={faSatellite} text={tech} backgroundColor={variables.findestPurple} color={"white"} title={"Predicted technology"} />);
                                })
                                :  <BorderedIconedTextblock key="loading" icon={faCircleNotch} iconClassName={`fa-spin ${styles.loading}`} text="Predicting technology..." title={"Predicting technology..."}/>
                            : null}
                        {predictedUnits ?
                            predictedUnits.map((unit, index) => {
                                return (<BorderedIconedTextblock key={index} onMouseUpHandler={setSelectedTextOnMouseEvent} icon={faRuler} text={unit} backgroundColor={variables.primaryPurple} color={"white"} title={"Specification"} />);
                            })
                            : null}
                    </div>
                    {patentDto.matchedHits && patentDto.matchedHits.length > 0 ?
                        <div className={styles.documentMetaDataContainer}>
                            {patentDto.matchedHits.map((matchedHit, index) => {
                                const correctMatchedHit = matchedHit as { [key: string]: any };
                                if(correctMatchedHit["item2"] === "ENV") {
                                    return (<BorderedIconedTextblock key={index} text={correctMatchedHit["item1"]} borderColor={variables.findestYellow} backgroundColor="white" color="black" title={"Specification"}/>); 
                                } else {
                                    return (<BorderedIconedTextblock key={index} text={correctMatchedHit["item1"]} borderColor={variables.findestBlue} backgroundColor="white" color="black"  title={"Specification"}/>);
                                }
                            })}
                        </div>
                        : null}
                </div>

            </div>
            {usPatent ?
                <PatentDocumentContent
                    patent={usPatent}
                    highlightedAbstract={highlightedAbstract}
                    highlightedClaims={highlightedClaims} />
                :
                <></>
            }
        </div>
    );
};